if (!customElements.get("custom-book-call-popup")) {
  class CustomBookCallPopup extends HTMLElement {
    constructor() {
      super();
      this.iti = null;
      this.previousPhoneValue = "";

      // Bind methods
      this.openPopup = this.openPopup.bind(this);
      this.closePopup = this.closePopup.bind(this);
      this.resetFormUI = this.resetFormUI.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.updateEmailVisibility = this.updateEmailVisibility.bind(this);
    }

    initElements() {
      if (this.initialized && this.popup) return true;

      this.popup = this.querySelector("[data-popup]");
      if (!this.popup) return false;

      this.overlay = this.querySelector("[data-overlay]");
      this.closeBtn = this.querySelector("[data-close-btn]");
      this.form = this.querySelector("[data-form]");
      this.submitBtn = this.querySelector("[data-submit-btn]");
      this.messageEl = this.querySelector("[data-message]");
      this.nameInput = this.querySelector('input[name="name"]');
      this.emailWrapper = this.querySelector("[data-email-wrapper]");
      this.emailInput = this.querySelector('input[name="email"]');
      this.phoneInput = this.querySelector('input[name="phone"]');
      this.mainArea = this.querySelector("[data-main-area]");
      this.successScreen = this.querySelector("[data-success-screen]");
      this.loadingScreen = this.querySelector("[data-loading-screen]");
      this.resetBtn = this.querySelector("[data-reset-btn]");

      this.initIntlTelInput();
      this.initCustomSelect();
      this.bindValidations();
      this.bindEvents();
      this.initialized = true;
      return true;
    }

    connectedCallback() {
      this.bindGlobalTriggers();

      // If the HTML parser is streaming, children may not exist yet when connectedCallback fires.
      // We try initializing now, and if children aren't in the DOM yet, retry on RAF and DOMContentLoaded.
      if (!this.initElements()) {
        requestAnimationFrame(() => this.initElements());
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", () => this.initElements(), { once: true });
        }
      }
    }

    initCustomSelect() {
      if (this.customSelectInitialized) return;
      const select = this.querySelector('select[name="concern"]');
      if (!select) return;
      if (this.querySelector('.custom-call-popup__custom-select')) {
        this.customSelectInitialized = true;
        return;
      }
      select.style.display = "none";

      const container = document.createElement("div");
      container.className =
        "custom-call-popup__custom-select font-family-avenir";

      const trigger = document.createElement("div");
      trigger.className = "custom-call-popup__custom-select-trigger";
      trigger.tabIndex = 0; // Make it focusable via Tab

      const triggerText = document.createElement("span");
      triggerText.className = "custom-call-popup__custom-select-text";
      triggerText.textContent = select.options[select.selectedIndex].text;

      trigger.appendChild(triggerText);
      container.appendChild(trigger);

      const list = document.createElement("ul");
      list.className = "custom-call-popup__custom-select-list";

      Array.from(select.options).forEach((option) => {
        if (option.value === "") return;
        const li = document.createElement("li");
        li.className = "custom-call-popup__custom-select-option";
        li.textContent = option.text;
        li.dataset.value = option.value;
        li.tabIndex = -1; // Focusable via script

        li.addEventListener("click", () => {
          triggerText.textContent = option.text;
          select.value = option.value;
          list.classList.remove("custom-call-popup__custom-select-list--open");
          trigger.classList.remove(
            "custom-call-popup__custom-select-trigger--open",
          );
        });

        li.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            li.click();
            trigger.focus();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = li.nextElementSibling;
            if (next) next.focus();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prev = li.previousElementSibling;
            if (prev) prev.focus();
            else trigger.focus();
          } else if (e.key === "Escape") {
            e.preventDefault();
            list.classList.remove(
              "custom-call-popup__custom-select-list--open",
            );
            trigger.classList.remove(
              "custom-call-popup__custom-select-trigger--open",
            );
            trigger.focus();
          }
        });

        list.appendChild(li);
      });

      container.appendChild(list);
      select.parentNode.insertBefore(container, select.nextSibling);

      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = list.classList.contains(
          "custom-call-popup__custom-select-list--open",
        );
        list.classList.toggle(
          "custom-call-popup__custom-select-list--open",
          !isOpen,
        );
        trigger.classList.toggle(
          "custom-call-popup__custom-select-trigger--open",
          !isOpen,
        );
      });

      trigger.addEventListener("keydown", (e) => {
        const isOpen = list.classList.contains(
          "custom-call-popup__custom-select-list--open",
        );
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          trigger.click();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          if (!isOpen) {
            trigger.click();
          }
          const firstOption = list.querySelector("li");
          if (firstOption) firstOption.focus();
        } else if (e.key === "Escape" && isOpen) {
          e.preventDefault();
          trigger.click();
        }
      });

      document.addEventListener("click", () => {
        list.classList.remove("custom-call-popup__custom-select-list--open");
        trigger.classList.remove(
          "custom-call-popup__custom-select-trigger--open",
        );
      });
      this.customSelectInitialized = true;
    }

    initIntlTelInput() {
      if (this.iti) return;
      const init = () => {
        if (!this.phoneInput) {
          this.phoneInput = this.querySelector('input[name="phone"]');
        }
        if (this.phoneInput && window.intlTelInput) {
          const defaultCountry = (this.dataset.defaultCountry || "in").toLowerCase();
          this.iti = window.intlTelInput(this.phoneInput, {
            initialCountry: defaultCountry,
            preferredCountries: [
              "in",
              "us",
              "de"
            ],
            separateDialCode: true,
            dropdownContainer: this.querySelector(
              ".custom-call-popup__phone-group",
            ),
          });
          this.previousPhoneValue = this.phoneInput.value;
          this.updateMaxLength();
          this.updateEmailVisibility();
          return true;
        }
        return false;
      };

      if (!init()) {
        const interval = setInterval(() => {
          if (init()) clearInterval(interval);
        }, 100);
      }
    }

    updateEmailVisibility() {
      if (!this.emailWrapper || !this.emailInput) return;

      const alwaysShow = this.dataset.showEmail === "true";
      if (alwaysShow) {
        this.emailWrapper.style.display = "";
        this.emailInput.required = true;
        return;
      }

      let isIndia = true;
      if (this.iti) {
        const countryData = this.iti.getSelectedCountryData();
        isIndia = countryData && countryData.iso2 ? countryData.iso2.toLowerCase() === "in" : true;
      }

      if (isIndia) {
        this.emailWrapper.style.display = "none";
        this.emailInput.required = false;
        const emailErrorEl = this.querySelector("[data-email-error]");
        if (emailErrorEl) {
          emailErrorEl.textContent = "";
          emailErrorEl.classList.remove("custom-call-popup__input-error--visible");
        }
        this.emailInput.style.borderColor = "";
      } else {
        this.emailWrapper.style.display = "";
        this.emailInput.required = true;
      }
    }

    updateMaxLength() {
      if (this.iti && this.phoneInput) {
        const countryData = this.iti.getSelectedCountryData();
        const rules = {
          in: { max: 10 },
          us: { max: 10 },
          de: { max: 11 },
          gb: { max: 10 },
          ae: { max: 9 },
          au: { max: 9 },
          fr: { max: 9 },
          ch: { max: 9 },
          za: { max: 9 },
          nl: { max: 9 },
          jp: { max: 10 },
        };

        if (rules[countryData.iso2]) {
          this.phoneInput.maxLength = rules[countryData.iso2].max;
        } else {
          this.phoneInput.maxLength = 15; // standard E.164 max length
        }
      }
    }

    bindValidations() {
      if (this.validationsBound) return;
      this.validationsBound = true;
      if (this.nameInput) {
        this.nameInput.addEventListener("input", (e) => {
          let validVal = e.target.value.replace(/[^a-zA-Z\s]/g, "");
          validVal = validVal.replace(/^\s+/, "");
          validVal = validVal.replace(/\s{2,}/g, " ");
          if (e.target.value !== validVal) {
            e.target.value = validVal;
          }
        });
      }

      if (this.emailInput) {
        this.emailInput.addEventListener("blur", () => {
          this.validateEmail(true);
        });
        this.emailInput.addEventListener("input", () => {
          const emailErrorEl = this.querySelector("[data-email-error]");
          if (emailErrorEl && emailErrorEl.classList.contains("custom-call-popup__input-error--visible")) {
            this.validateEmail(true);
          }
        });
      }

      if (this.phoneInput) {
        this.phoneInput.addEventListener("input", (e) => {
          // Keep formatting characters to avoid breaking intl-tel-input's internal state during browser autofill
          let validVal = e.target.value.replace(/[^\d\s\-\(\)\+]/g, "");
          if (e.target.value !== validVal) {
            e.target.value = validVal;
          }

          if (window.intlTelInputUtils && this.iti) {
            const countryData = this.iti.getSelectedCountryData();
            const iso = countryData.iso2;
            let cleanDigits = e.target.value.replace(/\D/g, "");

            const startRules = {
              in: /^[6789]/,
              us: /^[2-9]/,
              de: /^[1]/,
              gb: /^[7]/,
              ae: /^[5]/,
              au: /^[4]/,
              fr: /^[67]/,
              ch: /^[7]/,
              za: /^[678]/,
              nl: /^[6]/,
              jp: /^[789]/,
            };

            if (startRules[iso] && cleanDigits.length > 0) {
              // 1. Strip leading zeroes
              if (cleanDigits.startsWith("0")) {
                e.target.value = e.target.value.replace(/^[0\s\-\(\)]+/, "");
                cleanDigits = e.target.value.replace(/\D/g, "");
              }

              // 2. Validate starting digit
              if (
                cleanDigits.length > 0 &&
                !startRules[iso].test(cleanDigits)
              ) {
                e.target.value = ""; // clear if invalid start digit
              }
            }

            // Fallback logic to prevent overflowing max length, but ignore autofill injections
            const err = this.iti.getValidationError();
            if (
              !this.iti.isValidNumber() &&
              (err === window.intlTelInputUtils.validationError.TOO_LONG ||
                err === window.intlTelInputUtils.validationError.INVALID_LENGTH)
            ) {
              // Only revert if it's a manual physical keystroke, allowing autofill to paste freely
              if (e.inputType === "insertText") {
                e.target.value = this.previousPhoneValue;
              }
            } else {
              this.previousPhoneValue = e.target.value;
            }
          }
        });
        
        this.phoneInput.addEventListener("blur", () => {
          this.validatePhone(true);
        });

        this.phoneInput.addEventListener("countrychange", () => {
          this.previousPhoneValue = this.phoneInput.value;
          this.updateMaxLength();
          this.updateEmailVisibility();
        });
      }
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;
      if (this.closeBtn)
        this.closeBtn.addEventListener("click", this.closePopup);
      if (this.overlay) this.overlay.addEventListener("click", this.closePopup);
      if (this.resetBtn)
        this.resetBtn.addEventListener("click", this.resetFormUI);
      if (this.form)
        this.form.addEventListener("submit", this.handleFormSubmit);

      // Dynamically sync the width of the input for the country dropdown
      if (this.phoneInput) {
        const syncWidth = () => {
          if (this.phoneInput.offsetWidth > 0) {
            this.style.setProperty(
              "--phone-width",
              this.phoneInput.offsetWidth + "px",
            );
          }
        };
        window.addEventListener("resize", syncWidth);
        syncWidth();
      }

      // Accessibility: Keyboard Navigation & Focus Trapping
      document.addEventListener("keydown", (e) => {
        if (
          e.key === "Escape" &&
          this.popup &&
          this.popup.classList.contains("active")
        ) {
          this.closePopup();
        }
      });

      if (this.popup) {
        this.popup.addEventListener("keydown", (e) => {
          if (e.key === "Tab") {
            // Trap focus within the popup, filtering for currently visible elements
            const focusableElements = Array.from(
              this.popup.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
              ),
            ).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0);

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
              // Shift + Tab
              if (document.activeElement === firstElement) {
                lastElement.focus();
                e.preventDefault();
              }
            } else {
              // Tab
              if (document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
              }
            }
          }
        });
      }
    }

    bindGlobalTriggers() {
      // Ensures that no matter how many times this element is added to the page,
      // the document click listener is only attached once to open the first instance.
      if (!CustomBookCallPopup.triggersBound) {
        document.addEventListener("click", (e) => {
          const trigger = e.target.closest(".buttonPopup");
          if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            const popupInstance = document.querySelector(
              "custom-book-call-popup",
            );
            if (popupInstance) popupInstance.openPopup(trigger);
          }
        });
        CustomBookCallPopup.triggersBound = true;
      }
    }

    openPopup(triggerBtn) {
      this.triggerBtn = triggerBtn; // Store to return focus later
      this.initElements();
      if (this.popup) {
        this.popup.classList.add("active");
        this.updateEmailVisibility();

        if (this.phoneInput && this.phoneInput.offsetWidth > 0) {
          this.style.setProperty(
            "--phone-width",
            this.phoneInput.offsetWidth + "px",
          );
        }

        // Move reCAPTCHA badge into popup when opened (with polling if it hasn't loaded yet)
        const tryMoveBadge = () => {
          const badge = document.querySelector(".grecaptcha-badge");
          const container = this.querySelector("[data-recaptcha-container]");
          if (badge && container && badge.parentElement !== container) {
            badge.style.setProperty("position", "absolute", "important");
            badge.style.setProperty("left", "0px", "important");
            badge.style.setProperty("right", "auto", "important");
            badge.style.setProperty("bottom", "0px", "important");
            badge.style.setProperty("visibility", "visible", "important");
            badge.style.setProperty("opacity", "1", "important");
            badge.style.setProperty("transition", "none", "important");
            badge.style.setProperty("box-shadow", "none", "important");
            container.appendChild(badge);
            return true;
          } else if (badge && badge.parentElement === container) {
            return true;
          }
          return false;
        };

        if (!tryMoveBadge()) {
          const badgeInterval = setInterval(() => {
            if (tryMoveBadge() || !this.popup.classList.contains("active")) {
              clearInterval(badgeInterval);
            }
          }, 300);
        }
      }
    }

    closePopup() {
      if (this.popup) {
        this.popup.classList.remove("active");

        // Accessibility: Return focus to trigger button
        if (this.triggerBtn) {
          this.triggerBtn.focus();
        }

        // Restore reCAPTCHA badge to the inline form (or body) when popup closes
        const badge = document.querySelector(".grecaptcha-badge");
        if (badge && badge.parentElement !== document.body) {
          const inlineContainer = document.querySelector(".healing-journey-form__recaptcha-container");
          if (inlineContainer) {
            badge.style.setProperty("position", "absolute", "important");
            badge.style.setProperty("left", "0px", "important");
            badge.style.setProperty("right", "auto", "important");
            badge.style.setProperty("bottom", "0px", "important");
            inlineContainer.appendChild(badge);
          } else {
            badge.style.position = "";
            badge.style.left = "";
            badge.style.right = "";
            badge.style.bottom = "";
            badge.style.visibility = "";
            badge.style.opacity = "";
            badge.style.transition = "";
            badge.style.boxShadow = "";
            document.body.appendChild(badge);
          }
        }
        
        setTimeout(() => {
          this.resetFormUI();
        }, 400);
      }
    }

    resetFormUI() {
      if (this.form) this.form.reset();
      
      if (this.iti && this.phoneInput) {
        this.phoneInput.value = '';
        const defaultCountry = (this.dataset.defaultCountry || "in").toLowerCase();
        this.iti.setCountry(defaultCountry);
      }

      if (this.emailInput) {
        this.emailInput.value = '';
        const emailErrorEl = this.querySelector("[data-email-error]");
        if (emailErrorEl) {
          emailErrorEl.textContent = "";
          emailErrorEl.classList.remove("custom-call-popup__input-error--visible");
        }
        this.emailInput.style.borderColor = "";
      }

      this.updateEmailVisibility();

      if (this.phoneInput) {
        const phoneErrorEl = this.querySelector("[data-phone-error]");
        if (phoneErrorEl) {
          phoneErrorEl.textContent = "";
          phoneErrorEl.classList.remove("custom-call-popup__input-error--visible");
        }
        this.phoneInput.style.borderColor = "";
      }

      const select = this.querySelector('select[name="concern"]');
      const triggerText = this.querySelector(
        ".custom-call-popup__custom-select-text",
      );
      if (select && triggerText) {
        triggerText.textContent = select.options[0].text;
      }
      if (this.messageEl) {
        this.messageEl.className =
          "custom-call-popup__message font-family-avenir";
        this.messageEl.textContent = "";
      }
      if (this.mainArea) this.mainArea.classList.remove("hidden");
      if (this.successScreen) this.successScreen.classList.remove("active");
      if (this.loadingScreen) this.loadingScreen.classList.remove("active");
    }

    showMessage(text, type) {
      if (this.messageEl) {
        this.messageEl.textContent = text;
        this.messageEl.className = `custom-call-popup__message font-family-avenir custom-call-popup__message--${type}`;
      }
    }

    showLoadingScreen() {
      if (this.mainArea) this.mainArea.classList.add("hidden");
      if (this.successScreen) this.successScreen.classList.remove("active");
      if (this.loadingScreen) this.loadingScreen.classList.add("active");
    }

    hideLoadingScreen() {
      if (this.mainArea) this.mainArea.classList.remove("hidden");
      if (this.loadingScreen) this.loadingScreen.classList.remove("active");
    }

    showSuccessScreen() {
      if (this.mainArea) this.mainArea.classList.add("hidden");
      if (this.loadingScreen) this.loadingScreen.classList.remove("active");
      if (this.successScreen) this.successScreen.classList.add("active");
    }

    validateEmail(showError = true, isSubmit = false) {
      if (!this.emailInput) return true;

      const isVisible = this.emailWrapper ? this.emailWrapper.style.display !== "none" : (this.emailInput.offsetWidth > 0 || this.emailInput.required);
      const isRequired = this.emailInput.hasAttribute("required") || this.emailInput.required;

      if (!isVisible && !isRequired) {
        return true;
      }

      const email = this.emailInput.value.trim();
      const emailErrorEl = this.querySelector("[data-email-error]");
      
      const setError = (msg) => {
        if (showError && emailErrorEl) {
          if (msg) {
            emailErrorEl.textContent = msg;
            emailErrorEl.classList.add("custom-call-popup__input-error--visible");
            this.emailInput.style.borderColor = "#ff5252";
          } else {
            emailErrorEl.textContent = "";
            emailErrorEl.classList.remove("custom-call-popup__input-error--visible");
            this.emailInput.style.borderColor = "";
          }
        }
      };

      if (email === "") {
        if (isSubmit || isRequired) {
          setError("Please enter a valid email address.");
        } else {
          setError("");
        }
        return false; 
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Please enter a valid email address.");
        return false;
      }

      const parts = email.split(".");
      const tld = parts[parts.length - 1].toLowerCase();

      if (tld.length < 2 || tld.length > 10 || /(.)\1{4,}/.test(tld)) {
        setError("Please enter a valid email address with a proper domain.");
        return false;
      }

      if (typeof validTLDs !== "undefined" && !validTLDs.includes(tld)) {
        setError("Please enter a valid email address with a correct domain.");
        return false;
      }

      setError("");
      return true;
    }

    validatePhone(showError = true, isSubmit = false) {
      if (!this.phoneInput || !this.iti) return true;
      const phone = this.phoneInput.value.trim();
      const phoneErrorEl = this.querySelector("[data-phone-error]");
      
      const setError = (msg) => {
        if (showError && phoneErrorEl) {
          if (msg) {
            phoneErrorEl.textContent = msg;
            phoneErrorEl.classList.add("custom-call-popup__input-error--visible");
            this.phoneInput.style.borderColor = "#ff5252";
          } else {
            phoneErrorEl.textContent = "";
            phoneErrorEl.classList.remove("custom-call-popup__input-error--visible");
            this.phoneInput.style.borderColor = "";
          }
        }
      };
      
      if (phone === "") {
        if (isSubmit) {
          setError("Please enter a valid phone number");
        } else {
          setError("");
        }
        return false;
      }

      let cleanPhone = phone.replace(/\D/g, "");
      
      if (cleanPhone.length > 5 && /^(\d)\1+$/.test(cleanPhone)) {
        setError("Please enter a valid phone number (repeating digits are not allowed).");
        return false;
      }

      const countryData = this.iti.getSelectedCountryData();
      const iso = countryData.iso2;
      
      const startRules = {
        in: /^[6789]/,
        us: /^[2-9]/,
        de: /^[1]/,
        gb: /^[7]/,
        ae: /^[5]/,
        au: /^[4]/,
        fr: /^[67]/,
        ch: /^[7]/,
        za: /^[678]/,
        nl: /^[6]/,
        jp: /^[789]/,
      };

      if (startRules[iso] && cleanPhone.length > 0) {
        if (!startRules[iso].test(cleanPhone)) {
          setError(`Invalid phone number for ${countryData.name}. It should start with a valid mobile prefix.`);
          return false;
        }
      }

      if (!this.iti.isValidNumber()) {
        let errorMsg = "Please enter a valid phone number";
        if (window.intlTelInputUtils) {
          const errorCode = this.iti.getValidationError();
          switch (errorCode) {
            case window.intlTelInputUtils.validationError.IS_POSSIBLE:
              break;
            case window.intlTelInputUtils.validationError.INVALID_COUNTRY_CODE:
              errorMsg = "Invalid country code";
              break;
            case window.intlTelInputUtils.validationError.TOO_SHORT:
              errorMsg = "Phone number is too short";
              break;
            case window.intlTelInputUtils.validationError.TOO_LONG:
              errorMsg = "Phone number is too long";
              break;
            default:
              errorMsg = "Invalid phone number for the selected country";
          }
        }
        setError(errorMsg);
        return false;
      }
      
      setError("");
      return true;
    }

    async handleFormSubmit(e) {
      e.preventDefault();

      let isValid = true;
      const isEmailVisible = this.emailWrapper ? this.emailWrapper.style.display !== "none" : (this.emailInput && this.emailInput.required);
      if (this.emailInput && isEmailVisible && !this.validateEmail(true, true)) isValid = false;
      if (this.phoneInput && !this.validatePhone(true, true)) isValid = false;
      if (!isValid) {
        return;
      }
      const formData = new FormData(this.form);
      const data = Object.fromEntries(formData.entries());
      data.source = window.location.href;
      data.adminEmails = this.dataset.adminEmails || "";

      if (this.phoneInput && this.iti) {
        data.phone = "'" + this.iti.getNumber(); // Force Google Sheets to treat as text to preserve the + sign
        const countryData = this.iti.getSelectedCountryData();
        if (countryData && countryData.name) {
          data.country = countryData.name;
        }
      }

      if (data.name) data.name = data.name.trim();
      if (data.email) data.email = data.email.trim();

      if (data.concern === "") delete data.concern;
      if (data.details === "") delete data.details;

      if (!isEmailVisible || !data.email) {
        delete data.email;
      }

      // Push data to Google Tag Manager dataLayer immediately for conversion tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "custom_avg_form_submit",
        email: data.email || "",
        contact: data.phone ? data.phone.replace(/'/, "") : "",
        user_data: {
          email_address: data.email || "",
          phone_number: data.phone ? data.phone.replace(/'/, "") : ""
        }
      });

      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = "Submitting...";
      }
      this.showMessage("", "error"); // Clear previous messages

      // Immediately show the loading screen
      this.showLoadingScreen();
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = "Processing...";
      }


      // --- CONFIGURATION ---
      // Replace this with your Production AWS Endpoint later!
      const AWS_API_ENDPOINT =
        "https://oodfcfv5sj.execute-api.ap-south-1.amazonaws.com/prod/api/book-call";
      const AWS_API_KEY = "yXZzH1aIx36vweEoeILPP4BDH5LZvl9i52ttcvZm";
      // ---------------------

      const siteKey = this.dataset.siteKey || "";

      const sendPayload = (finalData) => {
        fetch(AWS_API_ENDPOINT, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": AWS_API_KEY,
          },
          body: JSON.stringify(finalData),
        })
          .then(async (response) => {
            const resData = await response.json();

            // Re-enable button
            if (this.submitBtn) {
              this.submitBtn.disabled = false;
              this.submitBtn.textContent =
                this.dataset.buttonText || "Submit Enquiry";
            }

            if (response.ok) {
              console.log("✅ Form Submission Success:", resData);
              this.showSuccessScreen();
            } else {
              console.error(
                "❌ Form Submission Failed:",
                response.status,
                resData,
              );
              this.hideLoadingScreen();
              this.showMessage(
                "Captcha verification failed. Please try again.",
                "error",
              );
            }
          })
          .catch((error) => {
            console.error("🚨 Error submitting form:", error);

            // Re-enable button on error
            if (this.submitBtn) {
              this.submitBtn.disabled = false;
              this.submitBtn.textContent =
                this.dataset.buttonText || "Submit Enquiry";
            }
            this.hideLoadingScreen();
            this.showMessage(
              "A network error occurred. Please try again.",
              "error",
            );
          });
      };

      if (siteKey && window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(siteKey, { action: "submit" })
            .then((token) => {
              data.recaptcha_token = token;
              sendPayload(data);
            })
            .catch((err) => {
              console.error("reCAPTCHA execution failed", err);
              sendPayload(data); // Send anyway if script fails
            });
        });
      } else {
        // Fallback if reCAPTCHA is not configured or failed to load
        sendPayload(data);
      }
    }
  }

  // Define the custom element
  customElements.define("custom-book-call-popup", CustomBookCallPopup);
}
